import DomainId from '@streem/domain-id';
import { VIDEO_HEIGHT, VIDEO_WIDTH } from '@streem/sdk-chime-video';
import { CameraSubject } from 'streem-sdk-protobuf';
import UAParser from './user_agent_parser';
import { logger } from './logger';
export async function generateStreemVideoTrackConstraints(cameraSubject, cameraPreference) {
    /**
     * ideal = If possible, one of the listed values will be used, but if it's not possible, the user agent will use the closest possible match.
     * exact = Either a single DOMString which must be the value of the property, or an array of DOMString objects one of which must be the property's value. If the property can't be set to one of the listed values, matching will fail.
     * https://developer.mozilla.org/en-US/docs/Web/API/ConstrainDOMString
     */
    return {
        width: { ideal: VIDEO_WIDTH },
        height: { ideal: VIDEO_HEIGHT },
        facingMode: { ideal: getFacingMode(cameraSubject) },
        deviceId: {
            exact: cameraPreference || (await getDeviceIdOverride(cameraSubject)),
        },
    };
}
/**
 * Converts a CameraSubject enum to a ConstrainDOMString for facing mode
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackConstraints/facingMode#value
 * @param cameraSubject CameraSubject
 * @returns string which is either 'user' or 'environment'. Defaults to 'environment'
 */
export function getFacingMode(cameraSubject) {
    if (cameraSubject === CameraSubject.CAMERA_SUBJECT_PERSON) {
        return 'user';
    }
    if (cameraSubject === CameraSubject.CAMERA_SUBJECT_WORLD) {
        return 'environment';
    }
    return 'environment';
}
export async function getDeviceIdOverride(cameraSubject) {
    // Need to force a camera override if this is a device known to have multiple back-facing cameras (Huawei, Kyocera)
    if (cameraSubject === CameraSubject.CAMERA_SUBJECT_WORLD) {
        const huaweiDeviceModelPrefixes = ['ELE-', 'EML-'];
        const device = UAParser.getDevice();
        if (huaweiDeviceModelPrefixes.some(pfx => device.model && device.model.startsWith(pfx))) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cameras = devices.filter(d => d.kind === 'videoinput');
            logger.info(`Device is ${device.model}. Forcing selection of camera ${cameras[cameras.length - 1].label}`);
            return cameras[cameras.length - 1].deviceId;
        }
    }
}
/**
 * The specific name that must be used for VideoTrack WallItems and VideoTracks
 * - `VideoTrack.name` should follow this pattern: `${deviceSid}:${cameraSubject}:${uniqueId}`. E.g. `dvc_123456abcdef:world:5`.
    - `uniqueId` can also be a UUID. It just has to exist to make the track names unique for this room.
    - `cameraSubject` is either `world` or `person` (lower-case version of the enum value from protobuf).
    - This pattern is chosen to make recording composition easier in the future. It should not be parsed for any purposes other than compositions.
 * @param deviceId DeviceID
 * @param cameraSubject CameraSubject
 * @returns string
 */
export function createVideoTrackName(deviceId, cameraSubject) {
    return `${deviceId.toString()}:${cameraSubject === CameraSubject.CAMERA_SUBJECT_PERSON ? 'person' : 'world'}:${new DomainId('', '').toString()}`;
}
//# sourceMappingURL=track_constraints.js.map